import React, { useState, useMemo } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import useAuth from '../../context/userContext'
import { ErrorNotification, SuccessNotification } from '../../utils'
import useSWR from 'swr'
import { BASE_URL, fetcher } from '../../utils/api'


const FarmerSMSModal = ({ userType, onClose }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [messageBody, setMessageBody] = useState('')
  const [title, setTitle] = useState('')
  const [errors, setErrors] = useState({})

  const { data: smsSenderIdsData, isLoading: loadingSenderIds, mutate: mutateSenderIds } = useSWR(`sms-sender-ids/`, fetcher)
  const smsSenderIds = Array.isArray(smsSenderIdsData) ? smsSenderIdsData : []
  const approvedSenderIds = useMemo(() => 
    smsSenderIds.filter(id => id.status === 'approved'),
    [smsSenderIds]
  )
  // if not approved we use this TESTSENDRID
  const defaultSenderId = useMemo(() => 
    approvedSenderIds[0]?.effective_sender_id || "Solidaridad",
    [approvedSenderIds]
  )



  const validate = () => {
    const newErrors = {}
    const totalLength = (title + messageBody).trim().length

    if (!title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (!messageBody.trim()) {
      newErrors.messageBody = 'Message is required'
    }

    if (totalLength > 100) {
      newErrors.general = `Total message (including title) too long. Maximum 160 characters allowed. Currently ${totalLength}.`
    }

    return newErrors
  }


  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors({})
    setLoading(true)

    try {
      const response = await fetch(
        `${BASE_URL}farmers-sms/${userType?.id}/send_sms/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            title: title,
            message: messageBody,
            sms_nickname: defaultSenderId,
            sms_sender_id: defaultSenderId
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData?.detail || errorData?.error || 'Failed to send SMS.')
      }

      // Success: Reset form and close modal
      SuccessNotification("Message submitted successful")
      setMessageBody('')
      onClose()

    } catch (err) {
      ErrorNotification(err.message || 'Something went wrong. Try again.')
    } finally {
      setLoading(false)
    }
  }


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-xl overflow-hidden shadow-lg w-full max-w-[550px] max-h-[60vh] relative">
        <div className='flex flex-row items-center justify-between border border-gray-200 dark:border-border-dark p-4'>
          <h2 className="text-xl font-bold dark:text-white">
            Send SMS
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <AiOutlineCloseCircle className="h-7 w-7" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='px-4 py-4'>
            <label className='uppercase text-sm font-medium text-gray-600 dark:text-border-dark'>
              SMS ID
            </label>
            <p className='text-sm uppercase font-medium text-black dark:text-white'>
              {defaultSenderId}
            </p>

            <div className="mb-4 mt-4">
              <label className="block text-base font-medium mb-1 dark:text-white">
                Title<span className='text-xs text-red-600'>*</span>
              </label>
              <input
                className={`w-full border ${errors.title ? 'border-red-500' : 'border-gray-200'} rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 dark:text-white dark:bg-secondaryDark dark:border-border-dark`}
                placeholder="Type message here"
                value={title}
                onChange={e => setTitle(e.target.value)}
                rows={3}
                name="title"
              />
              {errors.title && (
                <div className="text-xs text-red-500 mt-1">
                  {errors.title}
                </div>
              )}
            </div>


            <div className="mb-4 mt-4">
              <label className="block text-base font-medium mb-1 dark:text-white">
                Message<span className='text-xs text-red-600'>*</span>
              </label>
              <textarea
                className={`w-full border ${errors.messageBody ? 'border-red-500' : 'border-gray-200'} rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 dark:text-white dark:bg-secondaryDark dark:border-border-dark`}
                placeholder="Type message here"
                value={messageBody}
                onChange={e => setMessageBody(e.target.value)}
                rows={3}
                maxLength={100}
                name="messageBody"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {`${(title + messageBody).trim().length}/100 characters used`}
              </p>
              {errors.messageBody && (
                <div className="text-xs text-red-500 mt-1">
                  {errors.messageBody}
                </div>
              )}
            </div>

            {errors.general && (
              <div className="text-sm text-red-500 mb-2">
                {errors.general}
              </div>
            )}
          </div>

          <div className='border-t p-4 border-gray-200 flex justify-end bg-white dark:bg-dark dark:border-border-dark'>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {loading ? "Please wait..." : "Send"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FarmerSMSModal
